// src/pages/motolivraison/livreurs.page.jsx
//
// Liste de tous les livreurs MotoLivraison avec filtres et actions admin.

import React, { useEffect, useState, useCallback, useContext } from 'react';
import {
  Table, Modal, message, Button, Card, Row, Col, Tag, Space,
  Breadcrumb, Avatar, Select, Dropdown, Form, Input, InputNumber
} from 'antd';
import {
  CheckCircleOutlined, StopOutlined, EyeOutlined, PlayCircleOutlined,
  CarOutlined, RightOutlined, DownOutlined, ClockCircleOutlined,
  UserOutlined, UserAddOutlined, WalletOutlined, EnvironmentOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { motolivraisonService, citiesService } from '../../common/services';
import { formatPhoneCI } from '../../common/helper';
import {
  LOCALE_TABLE, PAGINATION_TABLE, BTN_ACTION, COULEURS_ACTION, cmpTexte, CarteStat,
} from './table.helpers';
import './css/motolivraison-table.css';
import { AuthContext } from '../../common/constants';

// Rôles à accès complet (comme le composant <Can/>).
const FULL_ACCESS_ROLES = ['superAdmin', 'directeur', 'support'];

const Livreurs = () => {
  const [livreurs, setLivreurs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [creating, setCreating] = useState(false);
  // Villes (filtre, création, correction de la ville d'affectation)
  const [cities, setCities] = useState([]);
  const [cityTarget, setCityTarget] = useState(null);
  const [cityForm] = Form.useForm();
  const [savingCity, setSavingCity] = useState(false);
  // Recharge en points
  const [rechargeTarget, setRechargeTarget] = useState(null);
  const [rechargeForm] = Form.useForm();
  const [recharging, setRecharging] = useState(false);
  const rechargeMontant = Form.useWatch('montant', rechargeForm);
  const navigate = useNavigate();

  const { currentUser } = useContext(AuthContext);
  const can = (perm) =>
    FULL_ACCESS_ROLES.includes(currentUser?.role) ||
    (currentUser?.permissions || []).includes(perm);

  const stats = {
    total: livreurs.length,
    pending: livreurs.filter(l => l.livreurMotoProfile?.status === 'pending').length,
    approved: livreurs.filter(l => l.livreurMotoProfile?.status === 'approved').length,
    actifs: livreurs.filter(l => l.livreurMotoProfile?.status === 'approved' && l.livreurMotoProfile?.isAvailable).length,
  };

  const fetchLivreurs = useCallback(async () => {
    setLoading(true);
    try {
      // Tri et filtres travaillent sur l'ensemble des livreurs (colonnes du tableau)
      const data = await motolivraisonService.listLivreurs({ limit: 1000 });
      setLivreurs(data?.livreurs || []);
    } catch (e) {
      message.error('Erreur lors du chargement des livreurs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLivreurs();
  }, [fetchLivreurs]);

  // Villes actives (filtre, création de livreur, correction de ville)
  useEffect(() => {
    (async () => {
      try {
        const res = await citiesService.getAll();
        const list = Array.isArray(res) ? res : (res?.data || res?.cities || []);
        setCities(list.filter(c => c?.isActive !== false));
      } catch (e) {
        console.error('Erreur chargement villes:', e);
      }
    })();
  }, []);

  const cityOptions = cities.map(c => ({ value: c.code, label: c.name }));

  const handleAction = async (action, livreur) => {
    Modal.confirm({
      title: `${action === 'validate' ? 'Approuver' : action === 'suspend' ? 'Suspendre' : 'Réactiver'} ce livreur ?`,
      content: `${livreur.firstName} ${livreur.lastName} (${livreur.phone})`,
      onOk: async () => {
        try {
          if (action === 'validate') await motolivraisonService.validateLivreur(livreur._id);
          else if (action === 'suspend') await motolivraisonService.suspendLivreur(livreur._id);
          else if (action === 'reactivate') await motolivraisonService.reactivateLivreur(livreur._id);
          message.success('Action effectuée');
          fetchLivreurs();
        } catch (e) {
          message.error(e?.message || 'Erreur');
        }
      },
    });
  };

  const openCreate = () => {
    createForm.resetFields();
    // Pré-sélection : ville par défaut de la plateforme (plus de ville codée en dur).
    const defaultCity = cities.find(c => c.isDefault) || cities[0];
    if (defaultCity) createForm.setFieldsValue({ cityCode: defaultCity.code });
    setCreateModalOpen(true);
  };

  // ─── Correction de la ville d'affectation (pilote le dispatch des missions) ───
  const openChangeCity = (livreur) => {
    setCityTarget(livreur);
    cityForm.setFieldsValue({ cityCode: livreur?.livreurMotoProfile?.cityId?.code });
  };

  const handleChangeCity = async () => {
    try {
      const { cityCode } = await cityForm.validateFields();
      setSavingCity(true);
      await motolivraisonService.updateLivreurCity(cityTarget._id, cityCode);
      message.success('Ville du livreur mise à jour');
      setCityTarget(null);
      fetchLivreurs();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Erreur lors du changement de ville');
    } finally {
      setSavingCity(false);
    }
  };

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      setCreating(true);
      const res = await motolivraisonService.createLivreurByAdmin(values);
      const successMsg = res?.userCreated
        ? `Livreur créé. Mot de passe par défaut : 123456`
        : (res?.profileAlreadyExists
            ? 'Cet utilisateur est déjà livreur approuvé'
            : 'Profil livreur ajouté à un utilisateur existant');
      message.success(successMsg);
      setCreateModalOpen(false);
      fetchLivreurs();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const openRecharge = (livreur) => {
    rechargeForm.resetFields();
    setRechargeTarget(livreur);
  };

  const handleRecharge = async () => {
    try {
      const values = await rechargeForm.validateFields();
      const montant = Number(values.montant);
      if (!montant) { message.warning('Montant invalide (différent de 0)'); return; }
      setRecharging(true);
      await motolivraisonService.rechargeLivreur(rechargeTarget._id, {
        montant,
        motif: values.motif?.trim() || undefined,
      });
      message.success(montant > 0 ? 'Livreur rechargé' : 'Points retirés');
      setRechargeTarget(null);
      fetchLivreurs();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Erreur lors de la recharge');
    } finally {
      setRecharging(false);
    }
  };

  const statusColor = (s) => ({ pending: 'orange', approved: 'green', suspended: 'red' })[s] || 'default';
  const statusLabel = (s) => ({ pending: 'En attente', approved: 'Approuvé', suspended: 'Suspendu' })[s] || s || '—';

  const columns = [
    {
      title: '', key: 'avatar', width: 60, align: 'center',
      render: () => <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />,
    },
    {
      title: 'Livreur', key: 'livreur', width: 220, ellipsis: true,
      sorter: (a, b) => cmpTexte(`${a.firstName} ${a.lastName}`, `${b.firstName} ${b.lastName}`),
      render: (_, r) => <span style={{ fontWeight: 600 }}>{`${r.firstName || ''} ${r.lastName || ''}`.trim() || '—'}</span>,
    },
    {
      title: 'Téléphone', key: 'tel', width: 150,
      render: (_, r) => <span style={{ whiteSpace: 'nowrap' }}>{formatPhoneCI(r.phone)}</span>,
    },
    {
      title: 'Ville', key: 'ville', width: 130,
      filters: cityOptions.map(o => ({ text: o.label, value: o.label })),
      onFilter: (value, r) => r.livreurMotoProfile?.cityId?.name === value,
      sorter: (a, b) => cmpTexte(a.livreurMotoProfile?.cityId?.name, b.livreurMotoProfile?.cityId?.name),
      render: (_, r) => r.livreurMotoProfile?.cityId?.name || '—',
    },
    {
      title: 'Statut', key: 'status', width: 130,
      filters: ['pending', 'approved', 'suspended'].map(v => ({ text: statusLabel(v), value: v })),
      onFilter: (value, r) => r.livreurMotoProfile?.status === value,
      render: (_, r) => {
        const st = r.livreurMotoProfile?.status;
        return <Tag color={statusColor(st)}>{statusLabel(st)}</Tag>;
      },
    },
    {
      title: 'Disponibilité', key: 'dispo', width: 150, align: 'center',
      filters: [{ text: 'Disponible', value: true }, { text: 'Indisponible', value: false }],
      onFilter: (value, r) => Boolean(r.livreurMotoProfile?.isAvailable) === value,
      render: (_, r) => {
        if (r.livreurMotoProfile?.status !== 'approved') return '—';
        const isAvailable = r.livreurMotoProfile?.isAvailable;
        return (
          <Tag color={isAvailable ? 'green' : 'default'}>
            {isAvailable ? '🟢 Disponible' : '⚫ Indisponible'}
          </Tag>
        );
      },
    },
    {
      title: 'Missions', key: 'missions', width: 110, align: 'center',
      sorter: (a, b) =>
        (a.livreurMotoProfile?.stats?.missionsCompletees || 0) - (b.livreurMotoProfile?.stats?.missionsCompletees || 0),
      render: (_, r) => r.livreurMotoProfile?.stats?.missionsCompletees || 0,
    },
    {
      title: 'Actions', key: 'actions', width: 360, align: 'center', fixed: 'right',
      render: (_, r) => {
        const status = r.livreurMotoProfile?.status;
        // Menu secondaire : actions moins fréquentes
        const items = [
          {
            key: 'city',
            icon: <EnvironmentOutlined style={{ color: COULEURS_ACTION.voir }} />,
            label: 'Changer la ville',
            onClick: () => openChangeCity(r),
          },
        ];
        if (can('motolivraison.livreurs:recharge')) {
          items.push({
            key: 'recharge',
            icon: <WalletOutlined style={{ color: '#fa8c16' }} />,
            label: 'Recharger en points',
            onClick: () => openRecharge(r),
          });
        }
        return (
          <Space size={6}>
            <Button
              size="small"
              icon={<EyeOutlined />}
              style={BTN_ACTION(COULEURS_ACTION.voir)}
              onClick={() => navigate(`/dashboard/motolivraison/livreurs/${r._id}`)}
            >
              Détails
            </Button>
            {status === 'pending' && (
              <Button size="small" icon={<CheckCircleOutlined />} style={BTN_ACTION(COULEURS_ACTION.valider)}
                onClick={() => handleAction('validate', r)}>
                Approuver
              </Button>
            )}
            {status === 'approved' && (
              <Button size="small" icon={<StopOutlined />} style={BTN_ACTION(COULEURS_ACTION.danger)}
                onClick={() => handleAction('suspend', r)}>
                Suspendre
              </Button>
            )}
            {status === 'suspended' && (
              <Button size="small" icon={<PlayCircleOutlined />} style={BTN_ACTION(COULEURS_ACTION.valider)}
                onClick={() => handleAction('reactivate', r)}>
                Réactiver
              </Button>
            )}
            <Dropdown menu={{ items }}>
              <Button size="small" style={BTN_ACTION('#334155')}>
                Plus <DownOutlined />
              </Button>
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item>Livreurs</Breadcrumb.Item>
        </Breadcrumb>

        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={6}><CarteStat label="Total" value={stats.total} icon={<CarOutlined />} /></Col>
          <Col xs={12} sm={6}><CarteStat label="En attente" value={stats.pending} icon={<ClockCircleOutlined />} color="#fa8c16" /></Col>
          <Col xs={12} sm={6}><CarteStat label="Approuvés" value={stats.approved} icon={<CheckCircleOutlined />} color="#52c41a" /></Col>
          <Col xs={12} sm={6}><CarteStat label="En ligne" value={stats.actifs} icon={<CheckCircleOutlined />} color="#1890ff" /></Col>
        </Row>

        <Card
          title="Livreurs MotoLivraison"
          extra={
            <Button type="primary" icon={<UserAddOutlined />} onClick={openCreate}>
              Nouveau livreur
            </Button>
          }
        >
          <Table
            className="moto-table"
            columns={columns}
            dataSource={livreurs.map(l => ({ ...l, key: l._id }))}
            loading={loading}
            size="middle"
            locale={LOCALE_TABLE}
            pagination={PAGINATION_TABLE('livreur')}
            scroll={{ x: 1410 }}
          />
        </Card>
      </div>

      {/* Modal Create Livreur */}
      <Modal
        title={
          <span>
            <UserAddOutlined style={{ marginRight: 8 }} />
            Nouveau livreur MotoLivraison
          </span>
        }
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onOk={handleCreate}
        okText="Créer"
        cancelText="Annuler"
        confirmLoading={creating}
        width={560}
      >
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            background: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: 6,
          }}
        >
          <strong>ℹ️ Mot de passe par défaut :</strong>{' '}
          <code style={{ background: '#fff', padding: '2px 8px', borderRadius: 4 }}>123456</code>
          <br />
          <small style={{ color: '#8c8c8c' }}>
            Le livreur pourra le changer après sa première connexion. Si le numéro existe déjà,
            le profil livreur sera ajouté à l'utilisateur existant (sans modifier son mot de passe).
          </small>
        </div>
        <Form form={createForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="Prénom"
                rules={[{ required: true, message: 'Prénom requis' }]}
              >
                <Input placeholder="Ex: Yao" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lastName" label="Nom">
                <Input placeholder="Ex: Konan" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="phone"
            label="Téléphone"
            rules={[{ required: true, message: 'Téléphone requis' }]}
            extra="Format : +225XXXXXXXX"
          >
            <Input placeholder="+225..." />
          </Form.Item>
          <Form.Item name="email" label="Email (optionnel)">
            <Input type="email" placeholder="email@exemple.com" />
          </Form.Item>
          <Form.Item
            name="cityCode"
            label="Ville"
            rules={[{ required: true, message: 'Ville requise' }]}
            extra="Ville d'affectation : le livreur ne reçoit que les commandes de cette ville."
          >
            <Select options={cityOptions} showSearch optionFilterProp="label" placeholder="Choisir la ville" />
          </Form.Item>
          <Form.Item name="numeroPermisConduire" label="Numéro de permis de conduire (optionnel)">
            <Input placeholder="Ex: 1234567890" />
          </Form.Item>
          <Form.Item name="plaqueImmatriculation" label="Plaque d'immatriculation (optionnel)">
            <Input placeholder="Ex: AA-123-CI" />
          </Form.Item>
          <Form.Item name="couleurMoto" label="Couleur de la moto (optionnel)">
            <Input placeholder="Ex: Rouge" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Changer la ville d'affectation */}
      <Modal
        title={
          <span>
            <EnvironmentOutlined style={{ marginRight: 8, color: '#1677ff' }} />
            Ville d'affectation
          </span>
        }
        open={!!cityTarget}
        onCancel={() => setCityTarget(null)}
        onOk={handleChangeCity}
        okText="Enregistrer"
        cancelText="Annuler"
        confirmLoading={savingCity}
      >
        <p style={{ marginBottom: 12, color: '#8c8c8c', fontSize: 13 }}>
          {cityTarget?.firstName} {cityTarget?.lastName} ({cityTarget?.phone}) — le livreur ne reçoit
          que les commandes de sa ville.
        </p>
        <Form form={cityForm} layout="vertical">
          <Form.Item
            name="cityCode"
            label="Ville"
            rules={[{ required: true, message: 'Ville requise' }]}
          >
            <Select placeholder="Choisir la ville" options={cityOptions} showSearch optionFilterProp="label" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Recharge Livreur en points (tracée) */}
      <Modal
        title={
          <span>
            <WalletOutlined style={{ marginRight: 8, color: '#fa8c16' }} />
            Recharger en points
          </span>
        }
        open={!!rechargeTarget}
        onCancel={() => setRechargeTarget(null)}
        onOk={handleRecharge}
        okText="Valider la recharge"
        cancelText="Annuler"
        confirmLoading={recharging}
        width={480}
        destroyOnClose
      >
        {rechargeTarget && (
          <>
            <div
              style={{
                marginBottom: 16,
                padding: 12,
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: 6,
              }}
            >
              <div style={{ fontWeight: 600 }}>
                {rechargeTarget.firstName} {rechargeTarget.lastName}
              </div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>{rechargeTarget.phone}</div>
              <div style={{ marginTop: 8 }}>
                Solde actuel :{' '}
                <strong>
                  {Number(rechargeTarget.soldePoints?.point ?? 0).toLocaleString('fr-FR')} pts
                </strong>
                {Number(rechargeTarget.soldePoints?.reserved) > 0 && (
                  <span style={{ color: '#8c8c8c' }}>
                    {' '}(dont {Number(rechargeTarget.soldePoints.reserved).toLocaleString('fr-FR')} réservés)
                  </span>
                )}
              </div>
            </div>
            <Form form={rechargeForm} layout="vertical">
              <Form.Item
                name="montant"
                label="Points à ajouter"
                extra="Ex : 5000 (ajoute) ou -2000 (retire)"
                rules={[{ required: true, message: 'Montant requis' }]}
              >
                <InputNumber style={{ width: '100%' }} step={500} placeholder="Ex : 5000" />
              </Form.Item>
              <Form.Item label="Nouveau solde (estimé)">
                <InputNumber
                  style={{ width: '100%' }}
                  disabled
                  value={Number(rechargeTarget.soldePoints?.point ?? 0) + Number(rechargeMontant || 0)}
                />
              </Form.Item>
              <Form.Item name="motif" label="Motif (optionnel)">
                <Input placeholder="Ex : Régularisation, bonus, correction..." />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
};

export default Livreurs;
